const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

export const getImageUrl = (imagePath, options = {}) => {
  if (!imagePath) return 'https://via.placeholder.com/400x300?text=No+Image';
  
  // If it's already a full URL (Cloudinary or external)
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    // For Cloudinary URLs, we can modify the URL to get different image qualities
    if (imagePath.includes('cloudinary.com') && options.fullResolution) {
      // Remove any existing transformations and get the original image
      return imagePath.replace(/\/upload\/[^/]+/, '/upload');
    }
    return imagePath;
  }
  
  // If it's a local path (starts with /uploads/)
  if (imagePath.startsWith('/uploads/')) {
    return `${API_URL}${imagePath}`;
  }
  
  // If it's just a filename or relative path
  return `${API_URL}/uploads/${imagePath}`;
};

/**
 * Extract the raw Cloudinary public ID from any Cloudinary URL,
 * stripping any existing transformation segments.
 * Works identically to the backend extractPublicId in routes/watermark.js.
 */
const extractCloudinaryPublicId = (imageUrl) => {
  try {
    const uploadIdx = imageUrl.indexOf('/upload/');
    if (uploadIdx === -1) return null;

    let afterUpload = imageUrl.slice(uploadIdx + '/upload/'.length);
    // Strip file extension
    afterUpload = afterUpload.replace(/\.[^/.]+$/, '');

    const segments = afterUpload.split('/');

    // Skip Cloudinary transformation segments (contain commas/colons or known prefixes)
    const transformPattern = /[,:]|^(w_|h_|c_|l_|g_|a_|o_|co_|e_|fl_|q_|f_|r_|b_|bo_|dpr_|z_|x_|y_|so_|du_|eo_|dl_|pg_|t_|vs_|ac_|af_|br_|fps_|vc_|vg_)/;

    const publicIdSegments = [];
    let pastTransforms = false;
    for (const seg of segments) {
      if (!pastTransforms && transformPattern.test(seg)) continue;
      pastTransforms = true;
      publicIdSegments.push(seg);
    }

    return publicIdSegments.join('/') || null;
  } catch {
    return null;
  }
};

/**
 * Build a Cloudinary delivery URL with a specific watermark text baked in.
 * Use this on the property details page to show "Tanavi Properties | Property ID: TPXXXXXX".
 * Falls back to the original URL if it's not a Cloudinary URL.
 *
 * @param {string} imageUrl  - The stored image URL (may already have a watermark transformation)
 * @param {string} propertyCode - e.g. "TP250026"
 */
export const buildDetailImageUrl = (imageUrl, propertyCode) => {
  if (!imageUrl) return 'https://via.placeholder.com/400x300?text=No+Image';
  if (!imageUrl.includes('cloudinary.com') || !propertyCode) return getImageUrl(imageUrl);

  const publicId = extractCloudinaryPublicId(imageUrl);
  if (!publicId) return getImageUrl(imageUrl);

  // Extract cloud name from the URL
  // URL format: https://res.cloudinary.com/<cloud_name>/image/upload/...
  const cloudNameMatch = imageUrl.match(/res\.cloudinary\.com\/([^/]+)\//);
  if (!cloudNameMatch) return getImageUrl(imageUrl);
  const cloudName = cloudNameMatch[1];

  const watermarkText = `Tanavi Properties | Property ID: ${propertyCode}`;

  // Encode the text for Cloudinary URL (spaces → _, special chars percent-encoded)
  const encodedText = encodeURIComponent(watermarkText)
    .replace(/%20/g, '_')
    .replace(/%7C/g, '%7C')  // keep pipe encoded
    .replace(/%3A/g, ':');   // colons are OK in Cloudinary text

  const transformation = [
    'w_1200,h_800,c_fill,g_auto',
    `l_text:Arial_40_bold:${encodedText},g_center,a_-30,o_50,co_white`,
  ].join('/');

  return `https://res.cloudinary.com/${cloudName}/image/upload/${transformation}/${publicId}`;
};

export const fetchWithTimeout = async (url, options = {}, timeout = 30000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...options.headers,
      }
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error('Request timeout - please check your internet connection');
    }
    throw error;
  }
};

export default API_URL;
